<template>
  <div class="add-course-page">
    <div class="header">
      <h1>Add a New CE Course</h1>
      <p class="subtitle">Share information about a continuing education course</p>
    </div>

    <form @submit.prevent="handleSubmit" class="course-form">
      <!-- Course Name -->
      <div class="form-group">
        <label for="courseName">Course Name <span class="required">*</span></label>
        <input
          type="text"
          id="courseName"
          v-model="formData.name"
          required
          placeholder="Enter the course name"
          class="form-input"
        />
      </div>

      <!-- Provider Search -->
      <ProviderSelector
        ref="providerSelector"
        label="Provider"
        :required="true"
        :require-postcode="true"
        input-id="addCourseProviderSearch"
        @provider-ready="handleProviderReady"
        @create-new-provider="handleCreateNewProvider"
      />

      <!-- Provider Form (shown when creating new provider) -->
      <div v-if="showProviderForm" class="provider-form-wrapper">
        <ProviderForm
          ref="providerForm"
          :initial-name="newProviderName"
          input-id="addCourseProviderForm"
          :require-postcode="true"
          @form-ready="handleProviderFormReady"
        />
        <div class="provider-form-actions">
          <button 
            type="button" 
            @click="handleCreateProvider" 
            :disabled="isCreatingProvider || !isProviderFormReady"
            class="btn-primary"
          >
            {{ isCreatingProvider ? 'Creating...' : 'Create Provider' }}
          </button>
          <button 
            type="button" 
            @click="handleCancelProviderForm"
            class="btn-secondary"
          >
            Cancel
          </button>
        </div>
      </div>

      <!-- Instructor -->
      <div class="form-group">
        <label for="instructor">Instructor (Optional)</label>
        <input
          type="text"
          id="instructor"
          v-model="formData.instructor"
          placeholder="Name of instructor"
          class="form-input"
        />
      </div>

      <!-- Credits -->
      <div class="form-group">
        <label for="credits">CE Credits <span class="required">*</span></label>
        <input
          type="number"
          id="credits"
          v-model.number="formData.credits"
          required
          min="0"
          step="0.01"
          placeholder="e.g. 2.25"
          class="form-input"
        />
        <small class="form-help">Enter the number of continuing education credits (up to 2 decimal places; courses can be worth quarters, e.g. 0.25, 0.5, 0.75)</small>
      </div>

      <!-- Cost -->
      <div class="form-group">
        <label for="cost">Cost <span class="required">*</span></label>
        <div class="cost-input-group">
          <select
            id="costType"
            v-model="formData.costType"
            required
            class="form-input cost-type"
          >
            <option value="free">Free</option>
            <option value="paid">Paid</option>
          </select>
          <input
            v-if="formData.costType === 'paid'"
            type="number"
            id="cost"
            v-model.number="formData.cost"
            required
            min="0"
            step="0.01"
            placeholder="0.00"
            class="form-input cost-amount"
          />
        </div>
      </div>

      <!-- Course / Activity Type -->
      <div class="form-group">
        <label for="courseType">Course / Activity Type <span class="required">*</span></label>
        <select
          id="courseType"
          v-model="formData.courseType"
          class="form-input"
          required
        >
          <option value="" disabled>Select activity type</option>
          <option
            v-for="type in courseTypes"
            :key="type"
            :value="type"
          >
            {{ type }}
          </option>
        </select>
      </div>

      <!-- Date/Time -->
      <div class="form-group">
        <label for="dateTime">Date and Time (Optional)</label>
        <input
          type="datetime-local"
          id="dateTime"
          v-model="formData.dateTime"
          class="form-input"
        />
        <small class="form-help">When is this course scheduled?</small>
      </div>

      <!-- Location -->
      <div class="form-group">
        <label for="location">Location (Optional)</label>
        <input
          type="text"
          id="location"
          v-model="formData.location"
          placeholder="City, State or full address"
          class="form-input"
        />
      </div>

      <!-- Website URL -->
      <div class="form-group">
        <label for="website">Website URL (Optional)</label>
        <input
          type="url"
          id="website"
          v-model="formData.website"
          placeholder="https://example.com"
          class="form-input"
        />
      </div>

      <!-- Description -->
      <div class="form-group">
        <label for="description">Description (Optional)</label>
        <textarea
          id="description"
          v-model="formData.description"
          rows="4"
          placeholder="Additional information about the course"
          class="form-input"
        ></textarea>
      </div>

      <!-- Form Actions -->
      <div class="form-actions">
        <button type="button" @click="handleCancel" class="btn btn-secondary">
          Cancel
        </button>
        <button type="submit" :disabled="isSubmitting" class="btn btn-primary">
          {{ isSubmitting ? 'Saving...' : 'Save Course' }}
        </button>
      </div>

      <!-- Success/Error Messages -->
      <div v-if="successMessage" class="message message-success">
        {{ successMessage }}
      </div>
      <div v-if="errorMessage" class="message message-error">
        {{ errorMessage }}
      </div>
    </form>
  </div>
</template>

<script>
import { saveCourse } from '../../services/courseService'
import { getCourseTypes, validateCourse } from '../../models/Course.js'
import ProviderSelector from '../shared/ProviderSelector.vue'
import ProviderForm from '../shared/ProviderForm.vue'
import { createProvider } from '../../services/providerService.js'

export default {
  name: 'AddCourse',
  components: {
    ProviderSelector,
    ProviderForm
  },
  props: {
    requireAuth: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      formData: {
        name: '',
        provider_id: null,
        instructor: '',
        credits: null,
        costType: 'free',
        cost: null,
        courseType: '',
        dateTime: '',
        location: '',
        website: '',
        description: ''
      },
      selectedProvider: null,
      isSubmitting: false,
      successMessage: '',
      errorMessage: '',
      showProviderForm: false,
      newProviderName: '',
      isCreatingProvider: false,
      isProviderFormReady: false
    }
  },
  computed: {
    courseTypes() {
      return getCourseTypes()
    }
  },
  methods: {
    handleProviderReady(provider) {
      this.selectedProvider = provider
      this.formData.provider_id = provider.id
      this.showProviderForm = false
      this.errorMessage = ''
    },
    handleCreateNewProvider(searchTerm) {
      this.showProviderForm = true
      this.newProviderName = searchTerm || ''
      this.selectedProvider = null
      this.formData.provider_id = null
    },
    handleProviderFormReady(isReady) {
      this.isProviderFormReady = isReady
    },
    async handleCreateProvider() {
      if (!this.$refs.providerForm) {
        this.errorMessage = 'Form not available.'
        return
      }

      const formData = this.$refs.providerForm.getFormData()

      // Validate required fields
      if (!formData.name || !formData.provider_type || 
          !formData.website || !formData.description) {
        this.errorMessage = 'Please fill in all required provider fields.'
        return
      }

      if (!formData.postcode) {
        this.errorMessage = 'Postcode is required.'
        return
      }

      this.isCreatingProvider = true
      this.errorMessage = ''

      try {
        const newProvider = await createProvider(formData)
        this.selectedProvider = newProvider
        this.formData.provider_id = newProvider.id
        this.showProviderForm = false
        this.newProviderName = ''
        
        // Update ProviderSelector to reflect the newly created provider
        if (this.$refs.providerSelector) {
          this.$refs.providerSelector.selectedProvider = newProvider
          this.$refs.providerSelector.providerSearchTerm = newProvider.name
          this.$refs.providerSelector.matchingProviders = []
        }
      } catch (error) {
        this.errorMessage = error.message || 'Failed to create provider. Please try again.'
        console.error('Error creating provider:', error)
      } finally {
        this.isCreatingProvider = false
      }
    },
    handleCancelProviderForm() {
      this.showProviderForm = false
      this.newProviderName = ''
      this.isProviderFormReady = false
      if (this.$refs.providerForm) {
        this.$refs.providerForm.reset()
      }
    },
    async handleSubmit() {
      this.isSubmitting = true
      this.successMessage = ''
      this.errorMessage = ''

      // Get provider from selector (either selected or create new one)
      let providerId = null
      try {
        if (!this.$refs.providerSelector) {
          this.errorMessage = 'Please search for and select a provider, or create a new one.'
          this.isSubmitting = false
          return
        }

        const provider = await this.$refs.providerSelector.getProvider()
        if (!provider || !provider.id) {
          this.errorMessage = 'Please search for and select a provider, or create a new one.'
          this.isSubmitting = false
          return
        }
        providerId = provider.id
      } catch (error) {
        this.errorMessage = error.message || 'Failed to process provider. Please try again.'
        console.error('Error processing provider:', error)
        this.isSubmitting = false
        return
      }

      // Validate course data (only basic fields, provider is handled separately)
      // Hours not shown in UI; we use credits for hours when creating
      const courseDataToValidate = {
        ...this.formData,
        hours: null,
        provider_id: providerId,
        providerType: this.selectedProvider?.provider_type || 'Other',
        providerName: this.selectedProvider?.name || 'Provider'
      }
      
      const validation = validateCourse(courseDataToValidate)
      if (!validation.isValid) {
        // Filter out provider-related errors since we handle provider separately
        const nonProviderErrors = validation.errors.filter(
          err => !err.toLowerCase().includes('provider')
        )
        if (nonProviderErrors.length > 0) {
          this.errorMessage = nonProviderErrors.join('. ') + '.'
          this.isSubmitting = false
          return
        }
      }

      try {
        // Prepare data for saving
        const courseData = {
          name: this.formData.name.trim(),
          provider_id: providerId,
          hours: this.formData.credits,
          credits: this.formData.credits,
          costType: this.formData.costType,
          courseType: this.formData.courseType,
          status: 'needs_review'
        }

        // Add optional fields if they have values
        if (this.formData.instructor && this.formData.instructor.trim()) {
          courseData.instructor = this.formData.instructor.trim()
        }
        if (this.formData.costType === 'paid' && this.formData.cost !== null) {
          courseData.cost = this.formData.cost
        }
        if (this.formData.dateTime) {
          courseData.dateTime = this.formData.dateTime
        }
        if (this.formData.location && this.formData.location.trim()) {
          courseData.location = this.formData.location.trim()
        }
        if (this.formData.website && this.formData.website.trim()) {
          courseData.website = this.formData.website.trim()
        }
        if (this.formData.description && this.formData.description.trim()) {
          courseData.description = this.formData.description.trim()
        }

        const savedCourse = await saveCourse(courseData)
        
        this.successMessage = 'Course saved successfully! Redirecting...'
        
        // Navigate to view course page after a short delay
        setTimeout(() => {
          this.resetForm()
          this.$emit('course-added', savedCourse.id)
        }, 1500)
      } catch (error) {
        this.errorMessage = 'Failed to save course. Please try again.'
        console.error('Error saving course:', error)
      } finally {
        this.isSubmitting = false
      }
    },
    handleCancel() {
      if (confirm('Are you sure you want to cancel? Any unsaved changes will be lost.')) {
        this.resetForm()
        this.$router.push({ name: 'landing' })
      }
    },
    resetForm() {
      this.formData = {
        name: '',
        provider_id: null,
        instructor: '',
        credits: null,
        costType: 'free',
        cost: null,
        courseType: '',
        dateTime: '',
        location: '',
        website: '',
        description: ''
      }
      this.selectedProvider = null
      this.successMessage = ''
      this.errorMessage = ''
      
      if (this.$refs.providerSelector) {
        this.$refs.providerSelector.reset()
      }
    }
  }
}
</script>

<style scoped>
.add-course-page {
  max-width: 800px;
  margin: 0 auto;
  padding: 40px 20px;
}

.header {
  text-align: center;
  margin-bottom: 40px;
}

.header h1 {
  font-size: 2rem;
  color: var(--color-text-primary);
  margin-bottom: 10px;
}

.subtitle {
  font-size: 1.1rem;
  color: var(--color-text-secondary);
  margin: 0;
}

.course-form {
  background: var(--color-surface-light);
  border-radius: 12px;
  padding: 40px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.form-group {
  margin-bottom: 25px;
}

.form-group label {
  display: block;
  font-weight: 600;
  color: var(--color-text-primary);
  margin-bottom: 8px;
  font-size: 1rem;
}

.required {
  color: var(--color-error-main);
}

.form-input {
  width: 100%;
  padding: 12px;
  border: 2px solid var(--color-border-light);
  border-radius: 6px;
  font-size: 1rem;
  font-family: inherit;
  background-color: var(--color-surface-light);
  color: var(--color-text-primary);
  transition: border-color var(--transition-normal);
}

.form-input:focus {
  outline: none;
  border-color: var(--color-primary-main);
}

.form-input:disabled {
  background-color: var(--color-surface-dark);
  cursor: not-allowed;
}

.form-help {
  display: block;
  margin-top: 5px;
  color: var(--color-text-secondary);
  font-size: 0.875rem;
}

.cost-input-group {
  display: flex;
  gap: 10px;
}

.cost-type {
  flex: 0 0 120px;
}

.cost-amount {
  flex: 1;
}

.radio-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.radio-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-weight: normal;
}

.radio-label input[type="radio"] {
  margin-right: 10px;
  width: 20px;
  height: 20px;
  cursor: pointer;
}

.radio-label span {
  color: var(--color-text-primary);
}

textarea.form-input {
  resize: vertical;
  min-height: 100px;
}

.form-actions {
  display: flex;
  gap: 15px;
  justify-content: flex-end;
  margin-top: 30px;
  padding-top: 30px;
  border-top: 2px solid var(--color-border-light);
}

.btn {
  padding: 12px 30px;
  font-size: 1rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  transition: all var(--transition-normal);
}

.btn-primary {
  background-color: var(--color-primary-main);
  color: var(--color-text-inverse);
}

.btn-primary:hover:not(:disabled) {
  background-color: var(--color-primary-dark);
}

.btn-primary:disabled {
  background-color: var(--color-border-main);
  cursor: not-allowed;
}

.btn-secondary {
  background-color: var(--color-surface-dark);
  color: var(--color-text-primary);
  border: 2px solid var(--color-border-main);
}

.btn-secondary:hover {
  background-color: var(--color-border-light);
}

.message {
  margin-top: 20px;
  padding: 15px;
  border-radius: 6px;
  font-weight: 500;
}

.message-success {
  background-color: var(--color-success-light);
  color: var(--color-success-dark);
  border: 1px solid var(--color-success-main);
}

.message-error {
  background-color: var(--color-error-light);
  color: var(--color-error-dark);
  border: 1px solid var(--color-error-main);
}

.provider-results {
  margin-top: 10px;
  border: 2px solid var(--color-border-light);
  border-radius: 6px;
  background: var(--color-surface-light);
  max-height: 300px;
  overflow-y: auto;
}

.provider-results-header {
  padding: 10px 15px;
  font-weight: 600;
  color: var(--color-text-primary);
  border-bottom: 1px solid var(--color-border-light);
  background: var(--color-surface-dark);
}

.provider-result-item {
  padding: 12px 15px;
  cursor: pointer;
  border-bottom: 1px solid var(--color-border-light);
  transition: background-color var(--transition-normal);
}

.provider-result-item:hover {
  background-color: var(--color-surface-dark);
}

.provider-result-item:last-child {
  border-bottom: none;
}

.provider-name {
  font-weight: 600;
  color: var(--color-text-primary);
  margin-bottom: 4px;
}

.provider-details {
  font-size: 0.875rem;
  color: var(--color-text-secondary);
  display: flex;
  gap: 8px;
  align-items: center;
}

.provider-type {
  padding: 2px 8px;
  background: var(--color-primary-light);
  color: var(--color-primary-dark);
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
}

.provider-location {
  color: var(--color-text-secondary);
}

.provider-results-footer {
  padding: 10px 15px;
  border-top: 1px solid var(--color-border-light);
  background: var(--color-surface-dark);
}

.selected-provider {
  margin-top: 10px;
  padding: 12px;
  background: var(--color-success-light);
  border: 2px solid var(--color-success-main);
  border-radius: 6px;
}

.selected-provider-info {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}

.provider-type-badge {
  padding: 4px 10px;
  background: var(--color-primary-main);
  color: var(--color-text-inverse);
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
}

.btn-link {
  background: none;
  border: none;
  color: var(--color-primary-main);
  cursor: pointer;
  text-decoration: underline;
  font-size: 0.875rem;
  padding: 0;
}

.btn-link:hover {
  color: var(--color-primary-dark);
}

.btn-link-small {
  background: none;
  border: none;
  color: var(--color-primary-main);
  cursor: pointer;
  text-decoration: underline;
  font-size: 0.75rem;
  padding: 0;
}

.btn-link-small:hover {
  color: var(--color-primary-dark);
}

.provider-form-section {
  margin-top: 20px;
  padding: 20px;
  background: var(--color-surface-dark);
  border-radius: 8px;
  border: 2px solid var(--color-border-main);
}

.section-title {
  font-size: 1.25rem;
  color: var(--color-text-primary);
  margin-bottom: 10px;
  font-weight: 600;
}

.section-description {
  color: var(--color-text-secondary);
  font-size: 0.9rem;
  margin-bottom: 20px;
  font-style: italic;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
}

.form-input.searching {
  border-color: var(--color-primary-main);
}

@media (max-width: 768px) {
  .course-form {
    padding: 20px;
  }

  .cost-input-group {
    flex-direction: column;
  }

  .cost-type {
    flex: 1;
  }

  .form-actions {
    flex-direction: column;
  }

  .btn {
    width: 100%;
  }

  .form-row {
    grid-template-columns: 1fr;
  }
}

.provider-form-wrapper {
  margin-top: 20px;
}

.provider-form-actions {
  display: flex;
  gap: 15px;
  justify-content: flex-end;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid var(--color-border-light);
}

.btn-primary {
  padding: 12px 24px;
  background-color: var(--color-primary-main);
  color: var(--color-text-inverse);
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 500;
  transition: all var(--transition-normal);
}

.btn-primary:hover:not(:disabled) {
  background-color: var(--color-primary-dark);
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.btn-primary:disabled {
  background-color: var(--color-border-light);
  color: var(--color-text-secondary);
  cursor: not-allowed;
  opacity: 0.6;
}

.btn-secondary {
  padding: 12px 24px;
  background-color: transparent;
  color: var(--color-text-primary);
  border: 1px solid var(--color-border-main);
  border-radius: 6px;
  cursor: pointer;
  font-size: 1rem;
  transition: all var(--transition-normal);
}

.btn-secondary:hover {
  background-color: var(--color-surface-dark);
  border-color: var(--color-border-dark);
}

@media (max-width: 768px) {
  .provider-form-actions {
    flex-direction: column;
  }

  .btn-primary,
  .btn-secondary {
    width: 100%;
  }
}
</style>

